import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useLocations } from "@/contexts/LocationContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, ChefHat, Package, Trash2, Wallet, FileBarChart2, Sparkles,
  FileSpreadsheet, LogOut, Menu, X, CreditCard, Crown, Users,
  ShieldCheck, Zap, Wrench, Building2, ChevronDown, HelpCircle, UserCircle, Check, FileText, QrCode,
  Scale, ClipboardList, BookOpen, Boxes, Truck, Download, AlertTriangle, Plug, Receipt, Clock
} from "lucide-react";
import { MariaBoton } from "@/pages/Maria";
import { ConfirmDeleteProvider } from "@/components/useConfirmDelete";
import CookieBanner from "@/components/CookieBanner";
import BrandLogo from "@/components/BrandLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import SkipLink from "@/components/SkipLink";

// Los `label` vienen de i18n (t(labelKey)) — aquí solo se define la clave de traducción,
// la ruta, el icono y el testid, que no cambian con el idioma.
const navGroups = [
  {
    id: "hoy", labelKey: "nav.groups.today",
    items: [
      { to: "/app",            labelKey: "nav.items.dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
      { to: "/app/flujo-caja", labelKey: "nav.items.cashflow",  icon: Wallet,          testid: "nav-flujo-caja" },
    ],
  },
  {
    id: "operaciones", labelKey: "nav.groups.operations",
    items: [
      { to: "/app/consumos", labelKey: "nav.items.utilities", icon: Zap,         testid: "nav-consumos" },
      { to: "/app/equipos",  labelKey: "nav.items.equipment", icon: Wrench,      testid: "nav-equipos" },
      { to: "/app/sanidad",  labelKey: "nav.items.health",    icon: ShieldCheck, testid: "nav-sanidad" },
    ],
  },
  {
    id: "herramientas", labelKey: "nav.groups.tools",
    items: [
      { to: "/app/analisis-ia", labelKey: "nav.items.aiAnalysis", icon: Sparkles,      testid: "nav-analisis-ia" },
      { to: "/app/plantillas",  labelKey: "nav.items.templates",  icon: FileSpreadsheet,testid: "nav-plantillas" },
      { to: "/app/qr-empleados", labelKey: "nav.items.qrEmployees", icon: QrCode, testid: "nav-qr-empleados" },
      { to: "/app/comparador-precios", labelKey: "nav.items.priceComparator", icon: Scale, testid: "nav-comparador-precios" },
    ],
  },
  {
    id: "personal", labelKey: "nav.groups.staff",
    items: [
      { to: "/app/empleados", labelKey: "nav.items.employees", icon: Users,    testid: "nav-empleados" },
      { to: "/app/nominas",   labelKey: "nav.items.payroll",   icon: FileText, testid: "nav-nominas" },
    ],
  },
  {
    id: "carta", labelKey: "nav.groups.menu",
    items: [
      { to: "/app/carta", labelKey: "nav.items.menuLabel", icon: BookOpen, testid: "nav-carta" },
      { to: "/app/escandallos", labelKey: "nav.items.recipeCost", icon: ChefHat, testid: "nav-escandallos" },
      { to: "/app/mermas",      labelKey: "nav.items.waste",      icon: Trash2,  testid: "nav-mermas" },
      { to: "/app/catalogo",    labelKey: "nav.items.catalog",    icon: Boxes,   testid: "nav-catalogo" },
      { to: "/app/proveedores", labelKey: "nav.items.suppliers",  icon: Truck,   testid: "nav-proveedores" },
      { to: "/app/inventario",  labelKey: "nav.items.inventory",  icon: Package, testid: "nav-inventario" },
    ],
  },
  {
    id: "informes", labelKey: "nav.groups.reports",
    items: [
      { to: "/app/explotacion", labelKey: "nav.items.plAccount",      icon: FileBarChart2, testid: "nav-explotacion" },
      { to: "/app/informe-compras", labelKey: "nav.items.purchaseReport", icon: ClipboardList, testid: "nav-informe-compras" },
      { to: "/app/informe-caja", labelKey: "nav.items.cashReport", icon: Receipt, testid: "nav-informe-caja" },
      { to: "/app/informe-personal", labelKey: "nav.items.personalReport", icon: Clock, testid: "nav-informe-personal" },
    ],
  },
];

// ── NavGroup: solo se abre al pinchar, mutual exclusivo ──────────────────────
function NavGroup({ group, onNavigate, isOpen, onToggle }) {
  const { t } = useTranslation();
  const location = useLocation();
  const hasActive = group.items.some((it) =>
    it.to && (it.end ? location.pathname === it.to : location.pathname.startsWith(it.to))
  );
  const open = hasActive || isOpen;

  return (
    <div>
      {/* `aria-expanded` refleja el estado REAL del grupo (`open = hasActive ||
          isOpen`), no solo el que fija el clic: un grupo con la ruta activa se
          pinta abierto sin que nadie lo haya pulsado, y ahí el lector de
          pantalla tiene que decir "expandido" igual. No lleva `aria-controls`:
          el <ul> solo existe en el DOM cuando el grupo está abierto, y apuntar
          a un id inexistente es peor que no ponerlo. */}
      <button
        onClick={onToggle}
        aria-expanded={open}
        data-testid={group.testid}
        className={`flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${
          hasActive ? "border-brand-terracotta bg-brand-terracotta/5 text-brand-ink" : "border-transparent text-brand-stone hover:text-brand-ink"
        }`}
      >
        {t(group.labelKey)}
        <ChevronDown size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-0.5 space-y-0.5 pb-1">
          {group.items.map((it) => (
            <li key={it.to || it.labelKey}>
              {it.to ? (
                <NavLink
                  to={it.to}
                  end={it.end}
                  onClick={onNavigate}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    it.linkClassName
                      ? it.linkClassName(isActive)
                      : `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                          isActive ? "bg-brand-cream text-brand-ink" : "text-brand-ink hover:bg-brand-cream/60"
                        }`
                  }
                >
                  <it.icon size={16} />
                  {t(it.labelKey)}
                </NavLink>
              ) : (
                <div
                  data-testid={it.testid}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wide text-brand-stone/70 cursor-default select-none"
                >
                  <span className="w-4" />
                  {t(it.labelKey)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LocationSwitcher({ onChange }) {
  const { t } = useTranslation();
  const { items, active_location_id, currentLocation, switchLocation } = useLocations();
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <div className="relative border-b border-brand-edge px-3 py-2" data-testid="location-switcher">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 rounded-md border border-brand-edge bg-brand-cream/60 px-3 py-3 text-sm hover:bg-brand-cream min-h-[52px]" data-testid="location-switcher-trigger">
        <span className="flex min-w-0 items-center gap-2">
          <Building2 size={20} className="shrink-0 text-brand-stone" />
          <span className="truncate text-left">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-brand-stone">{t("nav.localActive")}</span>
            <span className="block truncate text-base font-semibold text-brand-ink">{currentLocation?.name || "—"}</span>
          </span>
        </span>
        <ChevronDown size={20} className={`shrink-0 text-brand-stone transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-3 right-3 z-20 mt-1 rounded-md border border-brand-edge bg-card shadow-lg" data-testid="location-switcher-dropdown">
          <ul className="max-h-48 overflow-y-auto py-1">
            {items.map((loc) => {
              const active = loc.location_id === active_location_id;
              return (
                <li key={loc.location_id}>
                  <button onClick={async () => { setOpen(false); if (!active) { await switchLocation(loc.location_id); onChange?.(); } }} className={`flex w-full items-center justify-between gap-2 border px-3 py-2 text-sm hover:bg-brand-cream ${active ? "border-brand-terracotta bg-brand-terracotta/5 font-semibold text-brand-ink" : "border-transparent text-brand-ink"}`} data-testid={`location-option-${loc.location_id}`}>
                    <span className="truncate">{loc.name}</span>
                    {active && <Check size={16} className="shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-brand-edge p-2">
            <NavLink to="/app/perfil" onClick={() => setOpen(false)} className="block rounded-md border border-brand-terracotta bg-brand-terracotta/5 px-2.5 py-1 text-xs font-semibold text-brand-ink" data-testid="manage-locations-link">
              {t("nav.manageLocations")}
            </NavLink>
          </div>
        </div>
      )}
    </div>
  );
}

function TrialBanner({ sub }) {
  const { t } = useTranslation();
  if (!sub || sub.is_super_admin) return null;
  if (sub.status === "active") return null;
  const isExpired = sub.status === "expired";
  // H-09: la variante "expirada" ya no usa `text-destructive` como color de TEXTO
  // (falla AA) — pasa a `brand-ink`, verificado con la fórmula WCAG real sobre
  // el HSL de index.css: destructive/5% compuesto sobre brand-cream da ≈12,79:1
  // en claro y ≈14,34:1 en oscuro con brand-ink como texto (ambos AA con margen
  // amplio). Gana el mismo patrón icono+color que ya usaba "trial" (AlertTriangle,
  // aquí en text-destructive en vez de text-brand-orange).
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2 text-sm ${isExpired ? "border-destructive/20 bg-destructive/5 text-brand-ink" : "border-brand-orange/30 bg-brand-orange/10 text-brand-ink"}`} data-testid="trial-banner">
      <div className="flex items-center gap-2">
        <AlertTriangle size={16} className={`shrink-0 ${isExpired ? "text-destructive" : "text-brand-orange"}`} />
        {isExpired ? t("nav.trialBanner.expired") : t("nav.trialBanner.trial", { count: sub.days_left })}
      </div>
      {/* H-06: 83×28 hoy — el hitbox invisible extiende solo el alto (dentro del
          propio py-2 del banner, sin cruzar su borde) hasta los 44px de alto
          preferentes; el ancho (83px) ya supera de sobra el mínimo. gap-3 (12px,
          garantizado) hacia el bloque de texto de la izquierda no se toca. */}
      <NavLink to="/app/suscripcion" className="relative before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] rounded-md bg-brand-terracotta px-3 py-1.5 font-semibold text-white hover:bg-brand-terracottaDark text-xs text-on-terracotta" data-testid="trial-banner-cta">{t("nav.trialBanner.cta")}</NavLink>
    </div>
  );
}

function ImpersonationBanner({ user, checkAuth }) {
  const [leaving, setLeaving] = useState(false);
  if (!user?.impersonated_by) return null;

  const volver = async () => {
    setLeaving(true);
    try {
      await api.post("/admin/stop-impersonation");
      await checkAuth();
      window.location.href = "/app/admin";
    } catch (e) {
      setLeaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-300 bg-purple-100 px-4 py-2 text-sm text-purple-900 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-200" data-testid="impersonation-banner">
      <div>🕵️ Estás viendo la app como <strong>{user.name || user.email}</strong> (impersonación).</div>
      {/* H-06: 106×28 hoy — mismo patrón que trial-banner-cta, solo alto (el
          ancho ya supera de sobra el mínimo), dentro del propio py-2 del banner. */}
      <button
        onClick={volver}
        disabled={leaving}
        className="relative before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] rounded-md bg-purple-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-800 disabled:opacity-60 dark:bg-purple-600 dark:hover:bg-purple-500"
        data-testid="stop-impersonation-btn"
      >
        {leaving ? "Volviendo..." : "Volver a admin"}
      </button>
    </div>
  );
}

export default function AppLayout() {
  const { t } = useTranslation();
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen]             = useState(false);
  const [sub, setSub]               = useState(null);
  // Solo "hoy" abierto por defecto; mutual exclusivo al pinchar
  const [openGroupId, setOpenGroupId] = useState("hoy");

  // Expuesto vía useOutletContext() para que páginas hijas (p. ej.
  // SuscripcionExito) puedan forzar un refresco de `sub` tras confirmar un
  // pago, sin depender de F5 — AppLayout no se remonta al navegar entre
  // rutas de /app/*, así que sin esto el banner queda congelado.
  const refreshSub = useCallback(() => {
    return api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshSub();
  }, [refreshSub]);

  // María puede pedir que se abra un grupo del sidebar para señalar un botón dentro
  useEffect(() => {
    const onAbrirGrupo = (e) => {
      const grupo = e?.detail?.grupo;
      if (grupo) setOpenGroupId(grupo);
    };
    window.addEventListener("tah:abrir-grupo-sidebar", onAbrirGrupo);
    return () => window.removeEventListener("tah:abrir-grupo-sidebar", onAbrirGrupo);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };
  const isOnIA = location.pathname === "/app/analisis-ia";

  // Ancho en pantalla grande: cap SOLO en páginas de formulario/lectura (columna
  // que se ve estirada en monitores anchos). Las páginas con tablas de datos,
  // dashboard e informes van a ancho completo (una tabla quiere el ancho).
  // No hay max-width global a propósito.
  const NARROW_ROUTES = [
    "/app/analisis-ia", "/app/plantillas", "/app/qr-empleados", "/app/carta",
    "/app/comparador-precios", "/app/suscripcion", "/app/ayuda",
    "/app/servicios", "/app/perfil",
  ];
  const isNarrow = NARROW_ROUTES.some((r) => location.pathname.startsWith(r));

  const toggleGroup = (id) => setOpenGroupId((prev) => prev === id ? null : id);

  // Items del grupo "cuenta" — construidos aquí (no en `navGroups`, que es
  // constante de módulo) porque `nav-admin` depende de `user` (A3, criterio 2/3).
  // `nav-conecta-tu-caja` es visible para todos los clientes, sin condición de
  // rol/plan/suscripción (criterio 3).
  const cuentaItems = [
    { to: "/app/perfil",         icon: UserCircle, labelKey: "nav.account.profile",      testid: "nav-perfil" },
    { to: "/app/suscripcion",    icon: CreditCard, labelKey: "nav.account.subscription", testid: "nav-suscripcion" },
    { to: "/app/conecta-tu-caja", icon: Plug,       labelKey: "nav.account.connectCaja", testid: "nav-conecta-tu-caja" },
    { to: "/app/ayuda",          icon: HelpCircle,  labelKey: "nav.account.help",         testid: "nav-ayuda" },
    { to: "/app/descargar-app",  icon: Download,    labelKey: "nav.account.downloadApp",  testid: "nav-descargar-app" },
    ...(user?.role === "super_admin" ? [{
      to: "/app/admin", icon: Crown, labelKey: "nav.account.admin", testid: "nav-admin",
      // H-06: mismo px-3 py-3 que sus hermanos de arriba (vía el default de
      // `NavGroup`); lo único que cambia es el color, con el mismo par de
      // clases activo/inactivo que ya tenía antes de entrar en el colapsable.
      linkClassName: (isActive) => `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${isActive ? "bg-brand-forest/10 text-brand-forest" : "text-brand-forest hover:bg-brand-forest/5"}`,
    }] : []),
  ];

  const Sidebar = () => (
    <aside className="sticky top-0 flex h-dvh w-64 flex-col border-r border-brand-edge bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-brand-edge px-4 py-3">
        <NavLink to="/app" onClick={() => setOpen(false)} data-testid="sidebar-logo-home">
          <BrandLogo className="h-9 w-auto" />
        </NavLink>
        <NavLink to="/app/servicios" className="shrink-0 rounded-md bg-brand-terracotta px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-terracottaDark text-on-terracotta" data-testid="nav-asesoria-btn">
          {t("nav.advisory")}
        </NavLink>
        {/* H-06: hitbox ampliado sin tocar el tamaño visual (icono 18×18, el más
            pequeño de la auditoría). No llega a los 44×44 preferentes: a la
            izquierda solo hay gap-2 (8px, garantizado por CSS `gap`) hasta
            "Asesorías" (nav-asesoria-btn) — se extiende solo 4px para no invadirlo.
            Abajo hay ~21px hasta location-switcher-trigger (12px de este `py-3` +
            1px de borde + 8px del `py-2` de LocationSwitcher) — se extiende 12px,
            con margen de sobra. Arriba y a la derecha no hay vecino (son los
            bordes del propio header), así que ahí se extiende hasta el límite del
            propio contenedor (12px / 16px). Resultado: 38×42 real de clic.
            Mismo patrón que task-reparacion-marca-checkbox-componente (relative +
            before:absolute + before:-inset), aquí con offsets por lado en vez de
            uniformes por la asimetría de vecinos. */}
        <button
          onClick={() => setOpen(false)}
          className="relative before:absolute before:-left-1 before:-right-4 before:-top-3 before:-bottom-3 before:content-[''] lg:hidden"
          data-testid="sidebar-close-btn"
        ><X size={20} /></button>
      </div>

      <LocationSwitcher onChange={() => setOpen(false)} />

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {navGroups
          .filter((g) => g.id !== "operaciones" || user?.role === "super_admin")
          .map((group) => (
            <NavGroup
              key={group.id}
              group={group}
              onNavigate={() => setOpen(false)}
              isOpen={openGroupId === group.id}
              onToggle={() => toggleGroup(group.id)}
            />
          ))}

        {/* Grupo "cuenta": mismo patrón que los 6 de `navGroups` (NavGroup +
            `openGroupId`/`toggleGroup`, exclusión mutua sin estado nuevo —
            Arquitectura P1), pero se queda FUERA de esa constante y dentro de
            su propio `div` con borde superior (Arquitectura P2): así no hace
            falta tocar el `.map` ni el filtro por rol de arriba. `nav-admin`
            conserva su verde propio vía `linkClassName` (P2-b, criterio 29). */}
        <div className="pt-2 border-t border-brand-edge mt-2">
          <NavGroup
            group={{ id: "cuenta", labelKey: "nav.account.title", testid: "nav-group-cuenta", items: cuentaItems }}
            isOpen={openGroupId === "cuenta"}
            onToggle={() => toggleGroup("cuenta")}
            onNavigate={() => setOpen(false)}
          />
        </div>

        <div className="flex items-center gap-2 px-2 pt-2">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
      </nav>

      <div className="border-t border-brand-edge p-3">
        <div className="flex items-center gap-3 rounded-md px-3 py-2">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-forest text-sm font-bold text-white">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 truncate text-sm font-semibold" data-testid="user-name">
              {user?.name}
              {user?.role === "super_admin" && <Crown size={16} className="text-brand-forest" />}
            </div>
            <div className="truncate text-xs text-brand-stone" data-testid="user-business-name">
              {user?.business_name || <span className="italic opacity-70">{t("nav.noBusinessName")}</span>}
            </div>
          </div>
        </div>
        <button onClick={handleLogout} className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-3 text-sm font-medium text-brand-stone hover:bg-brand-cream" data-testid="logout-btn">
          <LogOut size={16} /> {t("nav.logout")}
        </button>
      </div>
    </aside>
  );

  return (
    <ConfirmDeleteProvider>
    <div className="flex min-h-screen bg-brand-cream">
      <SkipLink />
      <div className="hidden lg:block"><Sidebar /></div>

      {/* H-03: z-[60], estrictamente mayor que el z-50 de MariaPanel (Maria.jsx),
          para que el drawer (velo + Sidebar) siempre gane cuando está abierto —
          incluida María ya abierta antes. El botón de María y el FAB siguen en
          z-40, sin tocar Maria.jsx ni sus props. */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0"><Sidebar /></div>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-30 flex items-center justify-between border-b border-brand-edge bg-card px-4 py-2 lg:hidden">
          {/* H-06: p-3: icono 20 + padding 12×2 + borde 1×2 = 46×46 real (44×44
              cuando el icono era 18, 36×36 antes de H-06). El mínimo táctil de
              H-06 se mantiene: 46 ≥ 44. El separador de la derecha iguala el
              ancho REAL del botón (w-[46px], antes w-11=44px) para que el logo
              siga centrado de verdad, no solo aproximado — si cambia el tamaño
              del icono, este ancho se recalcula con él. */}
          <button onClick={() => setOpen(true)} className="shrink-0 rounded-md border border-brand-edge bg-card p-3" data-testid="mobile-menu-btn"><Menu size={20} /></button>
          <NavLink
            to="/app"
            data-testid="mobile-logo-home"
            className="flex flex-1 items-center justify-center py-1"
          >
            <BrandLogo className="h-10 w-auto" />
          </NavLink>
          <div className="w-[46px] shrink-0" />
        </div>
        <ImpersonationBanner user={user} checkAuth={checkAuth} />
        <TrialBanner sub={sub} />
        <CookieBanner />
        <div className="flex-1 p-4 pb-48 sm:p-6 sm:pb-48 lg:p-10 lg:pb-28">
          <div className={isNarrow ? "mx-auto w-full max-w-[1400px]" : ""}><Outlet context={{ refreshSub }} /></div>
        </div>
      </main>

      {!isOnIA && (
        <NavLink
          to="/app/analisis-ia"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-brand-terracotta px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-brand-terracottaDark transition-colors text-on-terracotta"
          data-testid="fab-ia"
        >
          <Sparkles size={16} />
          {t("nav.uploadInvoice")}
        </NavLink>
      )}

      <MariaBoton />
    </div>
    </ConfirmDeleteProvider>
  );
}
